import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Form, Formik } from "formik";

import {
  Autocomplete,
  Box,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  Typography,
} from "@mui/material";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

import FormField from "layouts/pages/users/new-user/components/FormField";

import { validationAdiantamento } from "./schemas/validationAdiantamento";
import formFieldAdiantamento from "./schemas/fieldAdiantamento";
import {
  ADIANTAMENTO_ACCENT,
  ADIANTAMENTO_ACCENT_SOFT,
  adiantamentoFieldSx,
  adiantamentoSectionSx,
} from "./adiantamento-evento.theme";

import { IEventoResponse } from "interfaces/eventos";
import { IAdiantamentoEvento } from "interfaces/adiantamento";

import { bytesToMB, currencyMaskBRL, currencyToNumber, getFileNameFromUrl } from "helpers/utils";

import {
  FormAdiantamentoValues,
  initialValuesFormAdiantamento,
  initialValuesFormAdiantamentoEdit,
} from "./schemas/initialValuesFormAdiantamento";

import {
  createAdiantamentoEvento,
  deleteFileComprovanteAdiantamento,
  editAdiantamentoEvento,
  uploadFileAdiantamento,
} from "services/adiantamento";
import { listarContasFinanceirasFinanceiro } from "layouts/pages/loja/financeiro/dashboard/services/contas-financeiras-financeiro.service";
import type { ContaFinanceiraFiltroApi } from "layouts/pages/loja/financeiro/dashboard/types/extrato-financeiro.types";

const {
  identificacaoTransacao,
  solicitante,
  dataSolicitacao,
  valorTransacao,
  dataPagamento,
  nomeResponsavel,
  celPhone,
  status,
  obs,
  contaFinanceiraId,
} = formFieldAdiantamento;

const STATUS_OPCOES: IAdiantamentoEvento["status"][] = [
  "Solicitado",
  "Agendado",
  "Pago",
  "Cancelado",
];

interface Props {
  evento: IEventoResponse;
  adiantamento: IAdiantamentoEvento | null;
  handleClose: () => void;
  updateEvento: () => void;
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <Typography variant="button" fontWeight={700} display="block" mb={1.5}>
      {children}
    </Typography>
  );
}

export default function FormAdiantamento({
  evento,
  adiantamento,
  handleClose,
  updateEvento,
}: Props) {
  const [filecomprovante, setFileComprovante] = useState<File | null>(null);
  const [filenameComprovanteCurrent, setFilenameComprovante] = useState<
    string | null
  >(adiantamento?.urlComprovante || null);
  const [contasFinanceiras, setContasFinanceiras] = useState<
    ContaFinanceiraFiltroApi[]
  >([]);
  const [contasLoading, setContasLoading] = useState(true);

  useEffect(() => {
    let ativo = true;
    setContasLoading(true);
    listarContasFinanceirasFinanceiro()
      .then((res) => {
        if (!ativo) return;
        setContasFinanceiras(res.data ?? []);
      })
      .finally(() => {
        if (ativo) setContasLoading(false);
      });
    return () => {
      ativo = false;
    };
  }, []);

  const contaPadraoId = useMemo(() => {
    const padrao = contasFinanceiras.find((conta) => conta.ehPadrao);
    return padrao?.id ?? contasFinanceiras[0]?.id ?? "";
  }, [contasFinanceiras]);

  const formInitialValues = useMemo(() => {
    const base = adiantamento
      ? initialValuesFormAdiantamentoEdit(adiantamento)
      : initialValuesFormAdiantamento;

    if (!base.contaFinanceiraId && contaPadraoId) {
      return { ...base, contaFinanceiraId: contaPadraoId };
    }

    return base;
  }, [adiantamento, contaPadraoId]);

  const handleRemoveFileComprovante = () => setFileComprovante(null);

  const handleDeleteFileComprovante = () => {
    setFilenameComprovante(null);
  };

  const handleFileComprovante = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0];
    if (file) setFileComprovante(file);
  };

  const toPayload = ({
    valorTransacao,
    contaFinanceiraId: contaId,
    status: statusValue,
    ...rest
  }: FormAdiantamentoValues): IAdiantamentoEvento => ({
    ...rest,
    status: statusValue,
    valorTransacao: currencyToNumber(valorTransacao),
    contaFinanceiraId:
      statusValue === "Pago" ? contaId?.trim() || null : null,
  });

  const handleSubmit = async (values: FormAdiantamentoValues) => {
    const params = toPayload(values);

    if (adiantamento) {
      let filenameComprovante = getFileNameFromUrl(adiantamento.urlComprovante);

      if (!filenameComprovanteCurrent && filenameComprovante) {
        await deleteFileComprovanteAdiantamento(filenameComprovante);
        filenameComprovante = "";
      }

      if (filecomprovante) {
        filenameComprovante = await uploadFileAdiantamento(filecomprovante);
      }

      const resp = await editAdiantamentoEvento({
        eventoId: evento.id,
        data: {
          ...params,
          id: adiantamento.id,
          urlComprovante: filenameComprovante,
        },
      });

      if (resp.status === 200) {
        handleClose();
        updateEvento();
      }
      return;
    }

    const resp = await createAdiantamentoEvento({
      data: params,
      eventoId: evento.id,
      file: filecomprovante,
    });

    if (resp.status === 201) {
      handleClose();
      updateEvento();
    }
  };

  const nomeArquivoExibicao = filecomprovante
    ? `${filecomprovante.name} · ${bytesToMB(filecomprovante.size)}`
    : filenameComprovanteCurrent
      ? getFileNameFromUrl(adiantamento?.urlComprovante)
      : null;

  return (
    <Formik<FormAdiantamentoValues>
      enableReinitialize
      initialValues={formInitialValues}
      validationSchema={validationAdiantamento}
      onSubmit={(values) => void handleSubmit(values)}
    >
      {({ values, errors, touched, isSubmitting, handleChange, setFieldValue, handleBlur }) => {
        const statusPago = values.status === "Pago";
        const opcoesConta = contasFinanceiras.map((conta) => ({
          value: conta.id,
          label: conta.tipo ? `${conta.nome} (${conta.tipo})` : conta.nome,
        }));
        const contaSelecionada =
          opcoesConta.find((opcao) => opcao.value === values.contaFinanceiraId) ??
          null;

        return (
        <Form id="form-adiantamento">
          <Stack spacing={2}>
            <Box sx={adiantamentoSectionSx}>
              <SectionTitle>Dados da transação</SectionTitle>
              <Grid container spacing={1.5}>
                <Grid item xs={12}>
                  <FormField
                    type={identificacaoTransacao.type}
                    label={identificacaoTransacao.label}
                    name={identificacaoTransacao.name}
                    required
                    variant="outlined"
                    value={values.identificacaoTransacao}
                    onChange={handleChange}
                    error={
                      errors.identificacaoTransacao &&
                      touched.identificacaoTransacao
                    }
                    sx={adiantamentoFieldSx}
                  />
                </Grid>
                <Grid item xs={12} sm={8}>
                  <FormField
                    type={solicitante.type}
                    label={solicitante.label}
                    name={solicitante.name}
                    required
                    variant="outlined"
                    value={values.solicitante}
                    onChange={handleChange}
                    error={errors.solicitante && touched.solicitante}
                    sx={adiantamentoFieldSx}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormField
                    type={dataSolicitacao.type}
                    label={dataSolicitacao.label}
                    name={dataSolicitacao.name}
                    required
                    variant="outlined"
                    value={values.dataSolicitacao}
                    onChange={handleChange}
                    error={errors.dataSolicitacao && touched.dataSolicitacao}
                    InputLabelProps={{ shrink: true }}
                    sx={adiantamentoFieldSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormField
                    type="text"
                    label={valorTransacao.label}
                    name={valorTransacao.name}
                    required
                    variant="outlined"
                    value={currencyMaskBRL(values.valorTransacao ?? "")}
                    onFocus={(event: React.FocusEvent<HTMLInputElement>) =>
                      event.target.select()
                    }
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      setFieldValue(
                        valorTransacao.name,
                        currencyMaskBRL(event.target.value)
                      )
                    }
                    onBlur={handleBlur}
                    error={errors.valorTransacao && touched.valorTransacao}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">R$</InputAdornment>
                      ),
                    }}
                    sx={adiantamentoFieldSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormField
                    type={dataPagamento.type}
                    label={dataPagamento.label}
                    name={dataPagamento.name}
                    required={statusPago}
                    variant="outlined"
                    onChange={handleChange}
                    value={values.dataPagamento}
                    error={errors.dataPagamento && touched.dataPagamento}
                    InputLabelProps={{ shrink: true }}
                    sx={adiantamentoFieldSx}
                  />
                </Grid>
                {statusPago ? (
                  <Grid item xs={12}>
                    <Autocomplete
                      options={opcoesConta}
                      loading={contasLoading}
                      value={contaSelecionada}
                      onChange={(_, option) => {
                        void setFieldValue(
                          contaFinanceiraId.name,
                          option?.value ?? "",
                        );
                      }}
                      isOptionEqualToValue={(option, value) =>
                        option.value === value.value
                      }
                      getOptionLabel={(option) => option.label}
                      renderInput={(params) => (
                        <FormField
                          {...params}
                          variant="outlined"
                          name={contaFinanceiraId.name}
                          required
                          label={contaFinanceiraId.label}
                          InputLabelProps={{ shrink: true }}
                          error={
                            Boolean(
                              errors.contaFinanceiraId &&
                                touched.contaFinanceiraId,
                            )
                          }
                          sx={adiantamentoFieldSx}
                        />
                      )}
                    />
                  </Grid>
                ) : null}
              </Grid>
            </Box>

            <Box sx={adiantamentoSectionSx}>
              <SectionTitle>Responsável e status</SectionTitle>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={4}>
                  <FormField
                    type={nomeResponsavel.type}
                    label={nomeResponsavel.label}
                    name={nomeResponsavel.name}
                    required
                    variant="outlined"
                    value={values.nomeResponsavel}
                    onChange={handleChange}
                    error={errors.nomeResponsavel && touched.nomeResponsavel}
                    sx={adiantamentoFieldSx}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormField
                    type={celPhone.type}
                    label={celPhone.label}
                    name={celPhone.name}
                    variant="outlined"
                    value={values.celPhone}
                    onChange={handleChange}
                    error={errors.celPhone && touched.celPhone}
                    sx={adiantamentoFieldSx}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Autocomplete
                    options={STATUS_OPCOES}
                    onChange={(_, newValue) => {
                      handleChange({
                        target: { name: status.name, value: newValue ?? "" },
                      });
                    }}
                    value={values.status}
                    renderInput={(params) => (
                      <FormField
                        {...params}
                        variant="outlined"
                        name={status.name}
                        required
                        label={status.label}
                        InputLabelProps={{ shrink: true }}
                        sx={adiantamentoFieldSx}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormField
                    type={obs.type}
                    label={obs.label}
                    name={obs.name}
                    variant="outlined"
                    value={values.obs}
                    onChange={handleChange}
                    error={errors.obs && touched.obs}
                    multiline
                    minRows={2}
                    sx={adiantamentoFieldSx}
                  />
                </Grid>
              </Grid>
            </Box>

            <Box sx={adiantamentoSectionSx}>
              <SectionTitle>Comprovante</SectionTitle>
              <Box
                sx={{
                  borderRadius: 2,
                  border: "2px dashed",
                  borderColor: nomeArquivoExibicao
                    ? "grey.300"
                    : "rgba(249, 96, 48, 0.45)",
                  bgcolor: nomeArquivoExibicao ? "grey.50" : ADIANTAMENTO_ACCENT_SOFT,
                  p: 2,
                  transition: "border-color 0.2s, background-color 0.2s",
                }}
              >
                {nomeArquivoExibicao ? (
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1.5}
                    alignItems={{ xs: "stretch", sm: "center" }}
                    justifyContent="space-between"
                  >
                    <Stack direction="row" spacing={1} alignItems="center" minWidth={0}>
                      <AttachFileOutlinedIcon sx={{ color: ADIANTAMENTO_ACCENT }} />
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {nomeArquivoExibicao}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.75}>
                      {adiantamento?.urlComprovante && !filecomprovante ? (
                        <MDButton
                          component={Link}
                          to={adiantamento.urlComprovante}
                          target="_blank"
                          variant="outlined"
                          color="dark"
                          size="small"
                          startIcon={<DownloadOutlinedIcon />}
                        >
                          Baixar
                        </MDButton>
                      ) : null}
                      <IconButton
                        color="error"
                        size="small"
                        onClick={
                          filecomprovante
                            ? handleRemoveFileComprovante
                            : handleDeleteFileComprovante
                        }
                        aria-label="Remover comprovante"
                      >
                        <DeleteOutlineOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Stack>
                ) : (
                  <Box
                    component="label"
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      py: 2,
                      cursor: "pointer",
                      textAlign: "center",
                    }}
                  >
                    <input
                      hidden
                      accept="application/pdf,image/*"
                      type="file"
                      onChange={handleFileComprovante}
                    />
                    <CloudUploadOutlinedIcon
                      sx={{ fontSize: 36, color: ADIANTAMENTO_ACCENT, mb: 0.75 }}
                    />
                    <Typography variant="body2" fontWeight={600}>
                      Clique para selecionar o comprovante
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      PDF ou imagem
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            <Divider />

            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <MDButton variant="text" color="secondary" onClick={handleClose} disabled={isSubmitting}>
                Cancelar
              </MDButton>
              <MDButton
                type="submit"
                variant="gradient"
                color="dark"
                disabled={isSubmitting}
                sx={{
                  minWidth: 160,
                  background: `linear-gradient(195deg, ${ADIANTAMENTO_ACCENT}, #e04a1a)`,
                }}
              >
                {isSubmitting ? (
                  <CircularProgress size={22} color="inherit" />
                ) : adiantamento ? (
                  "Salvar alterações"
                ) : (
                  "Salvar recebimento"
                )}
              </MDButton>
            </Stack>
          </Stack>
        </Form>
        );
      }}
    </Formik>
  );
}
